import { authConfig } from './authConfig';

class AuthService {
  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    const expiry = localStorage.getItem('token_expiry');
    if (!token || !expiry) return false;
    return Date.now() < parseInt(expiry);
  }

  getAccessToken(): string | undefined {
    return localStorage.getItem('access_token') || undefined;
  }

  getUserId(): string | undefined {
    const stored = localStorage.getItem('user_id') || undefined;
    if (stored) return stored;
    const uid = this.getUserIdFromToken(this.getAccessToken());
    if (uid) localStorage.setItem('user_id', String(uid));
    return uid;
  }

  async login(): Promise<void> {
    const { verifier, challenge } = await this.createPkce();
    const state = this.generateState();
    localStorage.setItem(`oauth_tx_${state}`, JSON.stringify({ verifier, ts: Date.now() }));

    const authUrl = this.buildAuthUrl(challenge, state);

    if (this.isInOffice()) {
      const bootUrl = this.buildDialogBootstrapUrl(state, verifier, authUrl);
      await this.openOfficeDialog(bootUrl);
      return;
    }
    window.location.href = authUrl;
  }

  private isInOffice(): boolean {
    return !!(window.Office?.context?.ui?.displayDialogAsync);
  }

  private buildAuthUrl(codeChallenge: string, state: string): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: authConfig.clientId,
      redirect_uri: authConfig.redirectUri,
      scope: authConfig.scopes.join(' '),
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });
    return `${authConfig.authority}/connect/authorize?${params.toString()}`;
  }

  private buildDialogBootstrapUrl(state: string, verifier: string, authUrl: string): string {
    const frag = new URLSearchParams({ s: state, v: verifier, u: encodeURIComponent(authUrl) }).toString();
    return `${window.location.origin}/auth-dialog-bootstrap.html#${frag}`;
  }

  private generateState(): string {
    return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }

  private async createPkce(): Promise<{ verifier: string; challenge: string }> {
    const verifier = this.base64Url(this.randomBytes(32));
    const challenge = this.base64Url(await this.sha256(verifier));
    return { verifier, challenge };
  }

  private randomBytes(length: number): Uint8Array {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return bytes;
  }

  private async sha256(input: string): Promise<ArrayBuffer> {
    const enc = new TextEncoder();
    return crypto.subtle.digest('SHA-256', enc.encode(input));
  }

  private base64Url(bufferOrString: ArrayBuffer | Uint8Array | string): string {
    let bytes: Uint8Array;
    if (typeof bufferOrString === 'string') bytes = new TextEncoder().encode(bufferOrString);
    else if (bufferOrString instanceof ArrayBuffer) bytes = new Uint8Array(bufferOrString);
    else bytes = bufferOrString;
    let str = btoa(String.fromCharCode(...Array.from(bytes)));
    return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  private decodeJwtPayload(token?: string | null): any | undefined {
    if (!token) return undefined;
    try {
      const base64 = token.split('.')[1];
      const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json);
    } catch { return undefined; }
  }

  private getUserIdFromToken(token?: string | null): string | undefined {
    const p = this.decodeJwtPayload(token);
    return p?.sub || p?.GEBRUIKER || p?.upn || p?.preferred_username || p?.email;
  }

  private openOfficeDialog(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.context.ui.displayDialogAsync(
        url,
        { height: 60, width: 50, displayInIframe: false },
        (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            return reject({ code: 'DIALOG_OPEN_FAILED', error: result.error?.code, message: result.error?.message });
          }
          const dialog = result.value;

          dialog.addEventHandler(Office.EventType.DialogMessageReceived, (event) => {
            try {
              if ('message' in event) {
                const data = JSON.parse(event.message);
                if (data.success) {
                  if (data.access_token) localStorage.setItem('access_token', data.access_token);
                  if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
                  if (data.expires_at) localStorage.setItem('token_expiry', String(data.expires_at));
                  else if (data.expires_in) localStorage.setItem('token_expiry', String(Date.now() + Number(data.expires_in) * 1000));
                  try {
                    const uid = this.getUserIdFromToken(data.access_token);
                    if (uid) localStorage.setItem('user_id', String(uid));
                  } catch {}
                }
                dialog.close();
                if (data.success) resolve();
                else reject({ code: 'AUTH_FAILED', error: data.error });
              } else {
                dialog.close();
                reject({ code: 'NO_MESSAGE', error: 'Event has no message property' });
              }
            } catch (err) {
              dialog.close();
              reject({ code: 'BAD_MESSAGE', error: err });
            }
          });

          dialog.addEventHandler(Office.EventType.DialogEventReceived, (event) => {
            dialog.close();
            if ('error' in event && event.error === 12006) reject({ code: 'USER_CLOSED', error: 12006 });
            else reject({ code: 'DIALOG_RUNTIME_ERROR', error: 'error' in event ? event.error : event.message });
          });
        }
      );
    });
  }

  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('user_id');

    const postLogout = encodeURIComponent(`${window.location.origin}/logout-callback.html`);
    const logoutUrl = `${authConfig.authority}/connect/endsession?post_logout_redirect_uri=${postLogout}`;

    if (this.isInOffice()) {
      this.openOfficeDialog(logoutUrl).catch(() => {});
      return;
    }
    window.location.href = logoutUrl;
  }
}

export const authService = new AuthService();
